// Closed-loop colony by-product simulation.
// Runs beside UnifiedSimulation without changing its core production/economy loop.
// Secondary outputs are intentionally non-blocking: when their storage is full,
// excess is simply not captured and the source building keeps operating.

import { useGame } from "../em2c-store";
import { EM2C_BUILDINGS } from "../data/em2c-buildings";

const TICK_MS = 250;
const DEFAULT_MAX = 250;
const HABITAT_WASTE = {
  co2: 0.0032,
  wastewater: 0.0024,
  organic_waste: 0.0008,
  waste: 0.0002,
};

let intervalId = null;
let lastAt = 0;

function ensureResource(resources, key) {
  if (resources[key]) return resources[key];
  return { stored: 0, max: DEFAULT_MAX };
}

function addCapped(resources, deltas, key, amount) {
  if (!Number.isFinite(amount) || amount <= 0) return;
  const current = ensureResource(resources, key);
  const max = Math.max(0, Number(current.max || DEFAULT_MAX));
  const room = Math.max(0, max - Number(current.stored || 0));
  const applied = Math.min(room, amount);
  resources[key] = { ...current, stored: Number(current.stored || 0) + applied, max };
  deltas[key] = (deltas[key] || 0) + applied;
}

function allocateResidents(buildings, colonistCount) {
  let remaining = Math.max(0, colonistCount);
  let changed = false;
  const next = buildings.map((building) => {
    const def = EM2C_BUILDINGS[building.type];
    const capacity = Number(def?.residentCapacity || 0);
    if (!capacity || building.progress < 1 || building.active === false) {
      if ((building.residents || 0) !== 0) changed = true;
      return (building.residents || 0) === 0 ? building : { ...building, residents: 0 };
    }
    const residents = Math.min(capacity, remaining);
    remaining -= residents;
    if ((building.residents || 0) !== residents) changed = true;
    return (building.residents || 0) === residents ? building : { ...building, residents };
  });
  return { buildings: changed ? next : buildings, housed: Math.max(0, colonistCount - remaining) };
}

function tickClosedLoop() {
  const now = typeof performance !== "undefined" ? performance.now() : Date.now();
  if (!lastAt) { lastAt = now; return; }
  const realDt = Math.min(1, Math.max(0, (now - lastAt) / 1000));
  lastAt = now;

  const state = useGame.getState();
  if (state.isPaused || state.victory || state.defeat) return;
  const dt = realDt * Math.max(0, Number(state.gameSpeed || 1));
  if (dt <= 0) return;

  const resources = Object.fromEntries(
    Object.entries(state.resources || {}).map(([key, value]) => [key, { ...value }])
  );
  const deltas = {};
  const colonistCount = state.colonists?.length || 0;
  const assigned = allocateResidents(state.buildings || [], colonistCount);

  // Resident-generated streams are attached to the habitat system. We only
  // capture waste for colonists who currently fit inside completed habitats.
  const lifeResearch = state.research?.completed || [];
  let lifeMult = 1;
  if (lifeResearch.includes("bio_life1")) lifeMult *= 0.85;
  if (lifeResearch.includes("bio_ecosystem1")) lifeMult *= 0.75;
  const housed = assigned.housed;
  if (housed > 0) {
    addCapped(resources, deltas, "co2", housed * HABITAT_WASTE.co2 * lifeMult * dt);
    addCapped(resources, deltas, "wastewater", housed * HABITAT_WASTE.wastewater * lifeMult * dt);
    addCapped(resources, deltas, "organic_waste", housed * HABITAT_WASTE.organic_waste * dt);
    addCapped(resources, deltas, "waste", housed * HABITAT_WASTE.waste * dt);
  }

  // Other buildings can expose logical by-products through nonBlockingOutputs.
  // A key already produced by the main productionOutputs is skipped to prevent
  // accidental double-production.
  for (const building of assigned.buildings) {
    if (building.progress < 1 || building.active === false || building.controllerShed) continue;
    const def = EM2C_BUILDINGS[building.type];
    if (!def?.nonBlockingOutputs) continue;
    const condition = Math.max(0, Number(building.condition ?? 1));
    const capacityMult = Math.max(0, Number(building.reducedCapacity || 1));
    for (const [key, rate] of Object.entries(def.nonBlockingOutputs)) {
      if (def.productionOutputs?.[key]) continue;
      addCapped(resources, deltas, key, Number(rate || 0) * 0.01 * condition * capacityMult * dt);
    }
  }

  const closedLoopRates = {};
  for (const [key, amount] of Object.entries(deltas)) {
    closedLoopRates[key] = realDt > 0 ? amount / realDt : 0;
  }

  useGame.setState({
    resources,
    buildings: assigned.buildings,
    _closedLoopNetRates: closedLoopRates,
  });
}

export function installClosedLoopSimulationIntegration() {
  if (intervalId || typeof window === "undefined") return () => {};
  lastAt = typeof performance !== "undefined" ? performance.now() : Date.now();
  intervalId = window.setInterval(tickClosedLoop, TICK_MS);
  return () => {
    if (intervalId) window.clearInterval(intervalId);
    intervalId = null;
    lastAt = 0;
  };
}
